import type { TextStyle, Point, Bounds } from 'pixi.js';
import { ColorMatrixFilter, Container, Sprite } from 'pixi.js';
import { DioramaPieceView } from './DioramaPieceView';
import type { DioramaConfig, DioramaPieceConfig } from '../../../app/state/diorama/dioramaState';
import { CurvedText } from '../common/text/CurvedText';
import { animateCurvedTextJiggle } from '../common/text/animateCurvedTextJiggle';
import { getLocalizedString } from '../../../app/localization/getLocalizedString';
import { animateBrightnessFlash } from '../../../utils/pixi/animations/filter-animation-utils';

interface DioramaViewConfig {
  config: DioramaConfig;
  progress: number;
  isLocked: boolean;
  hideRays?: boolean;
  hideBackground?: boolean;
}

interface PieceFragment {
  position: Point;
  increase(): void;
  isCompleted: boolean;
}

export class DioramaView extends Container {
  private readonly islandContainer = new Container().props({
    parent: this,
  });

  private readonly pieceContainer = new Container().props({
    parent: this.islandContainer,
  });

  private readonly pieces: DioramaPieceView[] = [];

  private readonly config: DioramaConfig;

  private titleText?: CurvedText;

  private background?: Container;

  constructor(viewConfig: DioramaViewConfig) {
    super();

    this.pieceContainer.sortableChildren = true;

    this.config = viewConfig.config;

    if (!viewConfig.hideRays) {
      this.addRays();
    }

    if (!viewConfig.hideBackground) {
      this.addBackground();
    }

    this.addIsland(this.config, viewConfig.isLocked);

    if (!viewConfig.isLocked) {
      this.initPieces(viewConfig.progress);
    }
  }

  public getConfig(): DioramaConfig {
    return this.config;
  }

  private addRays(): void {
    new Sprite()
      .props({
        parent: this,
        addAtIndex: 0,
        texture: 'New_Rays',
        y: -350,
        anchor: 0.5,
        blendMode: 'normal',
        scale: 2
      })
      .tweenTo(
        {
          angle: 360,
        },
        30000,
        'linear',
        true,
        0,
        -1,
      );
  }

  private addBackground(): void {
    this.background = new Container()
      .props({
        parent: this,
        addAtIndex: 0,
      });

    new Sprite()
      .props({
        parent: this.background,
        texture: 'Circle_Glow',
        tint: 0x7A0099,
        blendMode: 'normal',
        anchor: 0.5,
        y: -400,
        width: 1000,
        height: 1000
      });

    new Sprite()
      .props({
        parent: this.background,
        texture: 'Circle_Glow',
        tint: 0x5CB0FF,
        blendMode: 'normal',
        anchor: 0.5,
        y: -400,
        width: 900,
        height: 900
      });
  }

  public async hideBackground(): Promise<void> {
    const { background } = this;
    if (!background) return;

    await background.tweenTo({ alpha: 0 }, 100, 'quadOut', true).asyncOnComplete();

    background.destroy();
  }

  private addIsland(config: DioramaConfig, isLocked: boolean): void {
    const island = new Sprite().props({
      ...config.island,
      parent: this.pieceContainer,
      anchor: { x: 0.5, y: 1 },
    });
    
    if (isLocked) {
      const grayFilter = new ColorMatrixFilter();
      grayFilter.desaturate();
      grayFilter.enabled = true;

      island.filters = [grayFilter];
    }
  }

  private initPieces(initProgress: number): void {
    let progress = initProgress;
    this.config.pieces.forEach((pieceConfig, index) => {
      if (progress < 0) return;
      const { price } = pieceConfig;
      this.pieces[index] = this.initPiece(pieceConfig, Math.min(progress, price));
      progress -= price;
    });
  }

  private initPiece(pieceConfig: DioramaPieceConfig, initProgress: number): DioramaPieceView {
    return new DioramaPieceView(pieceConfig, initProgress).props({
      parent: this.pieceContainer,
      x: pieceConfig.x,
      y: pieceConfig.y,
      zIndex: pieceConfig.zIndex,
    });
  }

  public getNextPieceTarget(): PieceFragment | null {
    const pieceView = this.pieces[this.pieces.length - 1];
    if (pieceView.isCompleted()) return null;
    pieceView.increaseProgress();

    if (pieceView.isCompleted()) {
      this.checkNextPieceInit();
    }

    return {
      position: pieceView.getParticleTarget(),
      increase: (): void => {
        void pieceView.increaseProgressVisually();
      },
      isCompleted: pieceView.isCompleted(),
    };
  }

  private checkNextPieceInit(): void {
    const currentIndex = this.pieces.length - 1;
    if (!this.pieces[currentIndex].isCompleted()) return;

    const nextIndex = currentIndex + 1;
    if (!this.config.pieces[nextIndex]) return;
    this.pieces[nextIndex] = this.initPiece(this.config.pieces[nextIndex], 0).prepareForShow();
  }

  // to reveal next silhouette
  public showPieceIfHidden(): void {
    this.pieces[this.pieces.length - 1].animateShow();
  }

  public async showTitle(title: string, jiggleOffset: number, radius: number): Promise<void> {
    await this.hideTitle();

    const textStyle = {
      fontSize: 70,
      fontFamily: 'Baloo',
      fill: 'white',
      padding: 5,
      stroke: {
        color: 0xaaaaaa,
        width: 2,
      },
      align: 'left',
      dropShadow: {
        alpha: 1,
        angle: 0,
        color: 0xe84302,
        distance: 4,
        blur: 0,
      },
      trim: true,
      letterSpacing: 4,
    } as TextStyle;

    const titleText = new CurvedText({
      textStyle,
      text: getLocalizedString(title),
      radius,
    }).props({
      parent: this,
      y: 100 - radius,
      alpha: 0,
      scaleX: 0.85,
      scaleY: 0.85,
    });

    this.titleText = titleText;

    titleText.tweenTo(
      {
        alpha: 1,
      },
      333,
      'quadOut',
      true,
    );

    animateCurvedTextJiggle(titleText, jiggleOffset);

    await titleText
      .tweenTo(
        {
          scaleX: 1,
          scaleY: 1,
        },
        500,
        'backOut',
        true,
      )
      .asyncOnComplete();
  }

  public async hideTitle(): Promise<void> {
    const { titleText } = this;
    if (!titleText) return;

    await titleText.tweenTo({ alpha: 0 }, 333, 'quadOut', true).asyncOnComplete();

    titleText.destroy();
  }

  public hidePieces(duration = 333): void {
    this.pieceContainer.removeTweens();
    if (duration > 0) this.pieceContainer.tweenTo({ alpha: 0 }, 333, 'quadOut', true);
    else this.pieceContainer.alpha = 0;
  }

  public showPieces(duration = 333): void {
    this.pieceContainer.removeTweens();
    if (duration > 0) this.pieceContainer.tweenTo({ alpha: 1 }, 333, 'quadOut', true);
    else this.pieceContainer.alpha = 1;
  }

  public isCompleted(): boolean {
    return this.pieces.every((piece) => piece.isCompleted());
  }

  public animateIslandFlash(duration = 333, delay = 0, brightness = 1.25): void {
    animateBrightnessFlash(this.islandContainer, brightness, duration, 'quadOut', delay);
    this.islandContainer.tweenTo({ scaleX: 1.015, scaleY: 1.015 }, duration, 'quadOut', true, delay, undefined, true);
  }

  public getIslandBounds(): Bounds {
    return this.islandContainer.getLocalBounds();
  }

  public destroy(): void {
    super.destroy({ children: true });
  }
}
